import React, { useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import classes from './styles.module.scss';
import { actionNotifier } from '../../components/ui/toast';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '680px',
    maxWidth: 'calc(100vw - 32px)',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    maxHeight: '90vh',
    overflowY: 'auto',
    outline: 'none',
};

const defaultState = {
    id: null,
    name: '',
    designation: '',
    image: '',
    serialNumber: '',
};

const AboutUsFormModal = ({ open, handleClose, handleSave, onUploadFile, initialData, isLoading }) => {
    const [formData, setFormData] = useState(defaultState);
    const [isUploading, setIsUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');

    useEffect(() => {
        if (open && initialData) {
            setFormData({
                id: initialData.id || null,
                name: initialData.name || initialData.title || '',
                designation: initialData.designation || '',
                image: initialData.image || initialData.imageUrl || initialData.link || '',
                serialNumber: initialData.serialNumber || '',
            });
            setPreviewUrl(initialData.image || initialData.imageUrl || initialData.link || '');
            return;
        }

        if (open) {
            setFormData(defaultState);
            setPreviewUrl('');
        }
    }, [initialData, open]);

    const handleChange = (field, value) => {
        setFormData((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            actionNotifier.error('Only JPG, PNG, WEBP, or GIF images are allowed.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            actionNotifier.error('Image size must be less than 5MB.');
            return;
        }

        try {
            setIsUploading(true);
            const link = await onUploadFile(file);
            if (link) {
                setFormData((prevState) => ({ ...prevState, image: link }));
                setPreviewUrl(link);
                actionNotifier.success('Image uploaded successfully.');
            }
        } catch (error) {
            actionNotifier.error(error?.data?.message || 'Image upload failed.');
        } finally {
            setIsUploading(false);
        }
    };

    const onSubmit = async (event) => {
        event.preventDefault();

        if (!formData.name.trim() || !formData.designation.trim() || !formData.image || formData.serialNumber === '') {
            actionNotifier.error('Please add name, designation, serial number, and image.');
            return;
        }

        try {
            await handleSave(formData);
            handleClose();
        } catch (error) {
            actionNotifier.error(error?.data?.message || 'Unable to save About Us entry.');
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <div className={classes.modal_header}>
                    <h2 className={classes.modal_title}>
                        {initialData ? 'Edit About Us' : 'Add About Us'}
                    </h2>
                    <button onClick={handleClose} className={classes.close_modal_btn} type="button">
                        <CloseIcon />
                    </button>
                </div>

                <form onSubmit={onSubmit} className={classes.modal_form}>
                    <div className={classes.form_grid}>
                        <div className={`${classes.form_group} ${classes.form_group_full}`}>
                            <label>Name *</label>
                            <input
                                type="text"
                                placeholder="Enter name"
                                value={formData.name}
                                onChange={(event) => handleChange('name', event.target.value)}
                            />
                        </div>

                        <div className={`${classes.form_group} ${classes.form_group_full}`}>
                            <label>Designation *</label>
                            <input
                                type="text"
                                placeholder="Enter designation"
                                value={formData.designation}
                                onChange={(event) => handleChange('designation', event.target.value)}
                            />
                        </div>

                        <div className={`${classes.form_group} ${classes.form_group_full}`}>
                            <label>Serial Number * (for ordering)</label>
                            <input
                                type="number"
                                placeholder="Enter serial number"
                                value={formData.serialNumber}
                                onChange={(event) => handleChange('serialNumber', event.target.value)}
                            />
                        </div>

                        <div className={`${classes.form_group} ${classes.form_group_full}`}>
                            <label>Image *</label>
                            <label className={classes.upload_area} htmlFor="aboutus_file_input">
                                <CloudUploadIcon className={classes.upload_icon} />
                                <span className={classes.upload_text}>
                                    {isUploading ? 'Uploading...' : 'Click to upload image (JPG, PNG, WEBP)'}
                                </span>
                                <input
                                    id="aboutus_file_input"
                                    type="file"
                                    accept="image/*"
                                    className={classes.file_input_hidden}
                                    onChange={handleFileChange}
                                    disabled={isUploading}
                                />
                            </label>
                            {previewUrl ? (
                                <div className={classes.image_preview_wrapper}>
                                    <img
                                        src={previewUrl}
                                        alt="Preview"
                                        className={classes.image_preview}
                                    />
                                    <button
                                        type="button"
                                        className={classes.remove_image_btn}
                                        onClick={() => {
                                            setPreviewUrl('');
                                            handleChange('image', '');
                                        }}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className={classes.button_container}>
                        <button type="button" onClick={handleClose} className={classes.cancel_btn}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={classes.submit_btn}
                            disabled={isLoading || isUploading}
                        >
                            {isLoading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </Box>
        </Modal>
    );
};

export default AboutUsFormModal;
